<template>
  <u-modal
    :visible="stateOpen"
    :closable="closable"
    :width="width"
    :parent-dom="parentDom"
    vertical
    :top="top"
    :z-index="zIndex"
    :mask-closable="maskClosable"
    :footer-hide="hideFooter"
    :dialog-style="{
      maxWidth: '90vw'
    }"
    :class="modalClass"
    class="edit-dialog"
    @close="handleClose"
  >
    <template #header>
      <div class="modal-header">
        <div
          v-if="titleMode && title"
          class="header-title"
          :class="{ 'header-title--knowledge': knowledgeHeaderTitle }"
        >
          {{ title }}
        </div>
        <slot v-if="!titleMode" name="title" />
      </div>
      <slot name="upload-tips" />
    </template>

    <div
      class="common-modal-body"
      :class="{
        'pb-6': hideFooter,
        ['limit-height']: limitHeight,
        'is-flush-x': bodyInset === 'flush'
      }"
    >
      <slot />
      <ConfirmDialog
        v-if="isConfirm || submitConfirm"
        v-model:visible="visibility"
        width="360px"
        :top="220"
        footer-variant="saveCancel"
        :close-as-cancel="false"
        :content="currentConfirmText"
        :loading="loading"
        :mask-closable="false"
        @save="handleNestedSave"
        @cancel="handleNestedCancel"
        @close-icon="handleNestedCloseIcon"
      />
    </div>

    <slot name="tip" />
    <template v-if="!hideFooter" #footer>
      <div
        class="dialog-footer"
        :class="{ 'dialog-footer--align-start': footerAlign === 'start' }"
      >
        <slot name="btn" />
        <u-button
          v-if="isShowCancel"
          class="btn"
          style="margin-right: 12px"
          @click="handleCancel"
        >
          {{ cancelText }}
        </u-button>
        <u-button
          v-if="isShowSure"
          type="primary"
          class="sure-btn btn"
          :loading="loading"
          :disabled="sureButtonDisabled"
          @click="handleSubmit"
        >
          {{ sureBtnText }}
        </u-button>
      </div>
    </template>
  </u-modal>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { defineComponent, ref, watch, toRef, provide } from 'vue';
import ConfirmDialog from './ConfirmDialog.vue';

// Lets descendants (e.g. prompt editors) know they are rendered over a dialog.
export const EDIT_DIALOG_INJECT_KEY = Symbol('editDialogContext');

export default defineComponent({
  components: {
    ConfirmDialog
  },
  inheritAttrs: true,
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    closable: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: () => {
        const { t } = useI18n();
        return t('common.3b1c0e7488335a4f3b91a85979d0c296');
      }
    },
    limitHeight: {
      type: Boolean,
      default: true
    },
    sureBtnText: {
      type: String,
      default: () => {
        const { t } = useI18n();
        return t('common.670d03777d5b44d8f57ccd3293b9bdd3');
      }
    },
    sureBtnWidth: {
      type: Number,
      default: 120
    },
    cancelText: {
      type: String,
      default: () => {
        const { t } = useI18n();
        return t('common.124a48309c32c98c65a3f5c830338f54');
      }
    },
    isShowCancel: {
      type: Boolean,
      default: false
    },
    isShowSure: {
      type: Boolean,
      default: true
    },
    hideFooter: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    sureButtonDisabled: {
      type: Boolean,
      default: false
    },
    width: {
      type: [Number, String],
      default: 809
    },
    maskClosable: {
      type: Boolean,
      default: true
    },
    top: {
      type: Number,
      default: 100
    },
    titleMode: {
      type: Boolean,
      default: true
    },
    isConfirm: {
      type: Boolean,
      default: false
    },
    confirmText: {
      type: String,
      default: ''
    },
    submitConfirm: {
      type: Boolean,
      default: false
    },
    submitConfirmText: {
      type: String,
      default: ''
    },
    customClose: {
      type: Boolean,
      default: false
    },
    customCloseFn: {
      type: Function,
      default: () => {}
    },
    zIndex: {
      type: Number,
      default: 100
    },
    modalClass: {
      type: String,
      default: ''
    },
    parentDom: {
      type: String,
      default: ''
    },
    bodyInset: {
      type: String,
      default: 'default',
      validator: v => ['default', 'flush'].includes(v)
    },
    footerAlign: {
      type: String,
      default: 'end',
      validator: v => ['end', 'start'].includes(v)
    },
    knowledgeHeaderTitle: {
      type: Boolean,
      default: false
    }
  },
  emits: ['submit', 'update:visible', 'close', 'cancel'],
  setup(props, { emit }) {
    provide(EDIT_DIALOG_INJECT_KEY, true);
    const stateOpen = ref(props.visible);
    const handleCancel = () => {
      emit('cancel');
    };
    const visibility = ref(false);
    const confirmAction = ref(''); // 'close' | 'submit' | ''
    const handleSubmit = () => {
      if (props.loading) return;
      if (props.submitConfirm) {
        confirmAction.value = 'submit';
        visibility.value = true;
        return;
      }
      emit('submit');
    };
    const currentConfirmText = ref('');

    const handleNestedSave = () => {
      visibility.value = false;
      confirmAction.value = '';
      emit('submit');
    };

    const handleNestedCancel = () => {
      if (confirmAction.value === 'close') {
        stateOpen.value = false;
        emit('close');
        emit('update:visible', false);
        confirmAction.value = '';
        visibility.value = false;
        return;
      }
      visibility.value = false;
      confirmAction.value = '';
    };

    const handleNestedCloseIcon = () => {
      visibility.value = false;
      confirmAction.value = '';
    };

    watch(
      () => props.visible,
      val => {
        stateOpen.value = val;
      }
    );
    const handleClose = () => {
      if (props.customClose) {
        props.customCloseFn();
        return;
      }
      if (props.isConfirm) {
        confirmAction.value = 'close';
        visibility.value = true;
        return;
      }
      stateOpen.value = false;
      emit('close');
      emit('update:visible', false);
    };
    watch(
      () => [props.confirmText, props.submitConfirmText, confirmAction.value],
      () => {
        currentConfirmText.value =
          confirmAction.value === 'submit'
            ? props.submitConfirmText
            : props.confirmText;
      },
      { deep: true }
    );
    return {
      stateOpen,
      visibility,
      currentConfirmText,
      confirmAction,
      handleClose,
      handleCancel,
      handleSubmit,
      handleNestedSave,
      handleNestedCancel,
      handleNestedCloseIcon,
      knowledgeHeaderTitle: toRef(props, 'knowledgeHeaderTitle')
    };
  }
});
</script>

<style scoped lang="less">
.modal-header {
  display: flex;
  align-items: center;
  height: 100%;
  .header-title {
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    color: var(--title-color);
  }

  .header-title--knowledge {
    font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0;
    color: var(--text-neutral-color-text-primary);
  }
}

.common-modal-body {
  width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  :deep(.uui-form) {
    --form-item-margin-bottom: 20px;
  }

  &.limit-height {
    max-height: calc(85vh - 112px);
  }

  &.is-flush-x {
    padding-left: 0;
    padding-right: 0;
  }

  @media screen and (max-width: 768px) {
    padding-left: 16px;
    padding-right: 16px;

    &.is-flush-x {
      padding-left: 0;
      padding-right: 0;
    }
  }
}
.divider {
  height: 1px;
  width: 100%;
  background: var(--border-neutral-color-secondary);
  margin-top: 24px;
}
</style>

<style lang="less">
.edit-dialog {
  .uui-modal-footer {
    align-items: end;
    padding: 16px 24px 20px 24px;
  }

  .dialog-footer--align-start {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    gap: 8px;
  }
}

.uui-modal-footer:has(.dialog-footer--align-start) {
  justify-content: flex-start;
}
.uui-modal-body.edit-dialog {
  padding: 0 24px 20px 24px;
}
@media screen and (max-width: 768px) {
  .uui-modal {
    .uui-modal-close-x {
      height: 24px;
      width: 24px;
      display: flex;
      align-items: center;
    }
  }
}
.check-tip-normal-retrieval {
  padding-bottom: 24px;
}
.check-tip-structure-retrieval {
  padding-bottom: 0px !important;
  margin: -10px 0px -6px 0px;
}
.check-tip-tree-retrieval {
  padding-bottom: 0px !important;
}
</style>
